import {PaletteOptions} from '@mui/material';
import {grey} from '@mui/material/colors';
import {colors} from '@ark/react';
import {ChartPalette, chartPalette} from './chart';

declare module '@mui/material/styles' {
  interface Palette {
    pill: {
      yellow: string;
      pink: string;
      purple: string;
    };
    chart: ChartPalette;
  }
  interface PaletteOptions {
    pill: {
      yellow: string;
      pink: string;
      purple: string;
    };
    chart?: ChartPalette;
  }
  interface PaletteColor {
    faded?: string;
  }
  interface SimplePaletteColorOptions {
    faded?: string;
  }
  interface TypeText {
    alternative: string;
  }
  interface CommonColors {
    slate: string;
  }
}

const pill = {
  yellow: colors.selectiveYellow,
  pink: colors.pink,
  purple: colors.fadedPurple,
};

const text = {
  alternative: colors.black,
};

const common = {
  slate: colors.slate,
};

const light: PaletteOptions = {
  primary: {
    light: colors.twitterBlue,
    main: colors.buttonBlue,
    faded: colors.buttonBlue,
  },
  secondary: {
    main: colors.brick,
  },
  success: {
    main: colors.caribbeanGreen,
  },
  warning: {
    light: colors.saffron,
    main: colors.jaffa,
    dark: colors.goldDrop,
    contrastText: colors.white,
  },
  action: {
    active: colors.black,
    hover: grey[200],
  },
  error: {
    main: colors.carnation,
  },
  background: {
    default: colors.white,
    paper: grey[50],
  },
  info: {
    main: colors.twitterBlue,
    faded: colors.fadedBlue,
  },
  pill,
  text,
  common,
  chart: chartPalette.light,
};

const dark: PaletteOptions = {
  primary: {
    main: colors.brick,
    faded: colors.buttonBlue,
  },
  secondary: {
    main: colors.white,
  },
  success: {
    main: colors.caribbeanGreen,
    dark: colors.japaneseLaurel,
  },
  warning: {
    main: colors.jaffa,
    dark: colors.goldDrop,
    contrastText: colors.white,
  },
  action: {
    active: colors.black,
    hover: '#1e1e1e',
  },
  error: {
    main: colors.carnation,
  },
  background: {
    default: '#2e2e2e',
    paper: '#121212',
  },
  grey: {
    '100': '#212121',
    '200': '#424242',
    '300': '#616161',
    '400': '#bdbdbd',
    '600': '#e0e0e0',
    '700': '#eeeeee',
    '800': '#f5f5f5',
    '900': '#fafafa',
  },
  pill,
  chart: chartPalette.dark,
};

const withAccent = (palette: PaletteOptions, accent: string) => {
  const chart = palette.chart && {
    ...palette.chart,
    rank: {...palette.chart.rank, attention: accent},
  };
  if (palette === dark) {
    return {...palette, primary: {...palette.primary, main: accent}, chart};
  }
  return {...palette, secondary: {main: accent}, chart};
};

export const createPalette = (
  mode: 'light' | 'dark',
  accent: string = colors.brick,
) => {
  const palette = mode === 'dark' ? dark : light;
  return {mode, ...withAccent(palette, accent)};
};
